import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';

import { FONT } from '../../styles/color';

import Section from '../../components/layout/Section';
import Calculator from '../../components/UI/Calculator';
import PageTitle from '../../components/UI/PageTitle';
import CartList from '../../components/UI/CartList';
import { FormTitle, Field, AddressField, FormButton, FieldContainer, DropDownField, TextField } from '../../components/UI/Form';
import FormContainer from '../../components/UI/FormContainer';
import { Medium } from '../../components/UI/Text';
import { RECEIPT_TYPE } from '../../utils/constants';
import { useFetch } from '../../hooks/useFetch';
import { userApi } from '../../api/user';
import { orderApi } from '../../api/order';

const OrderContainer = styled.div`
	width: 90%;
	max-width: 1320px;
    display: flex;
    flex-direction: row;
    gap: 50px;
    margin-bottom: 100px;

	@media (max-width: 1080px) {
		flex-direction: column;
		align-items: stretch;
		gap: 30px;
	}
`;

const LeftSection = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    min-width: 480px;
    width: 100%;
    flex-grow: 1;
    flex-shrink: 1;
	gap: 10px;
`;

const RightSection = styled.div`
    width: 370px;
    height: fit-content;
    position: sticky;
    top: 324px;
    right: 0px;
    flex-shrink: 0;

    @media (max-width: 1080px) {
		position: relative;
        top: auto;
        right: auto;
        width: 100%;
	}
`;

const FormTitleWrapper = styled.div`
	display: flex;
	flex-direction: row;
	gap: 25px;
	align-items: flex-end;
`;

// orderItem 구조
// {
//     productCode: 123,
//     productName: '6.5oz 무지',
//     productThumbnail: 'image src',
//     price: 13000,
//     shippingFee: 2500,
//     quantity: 1
// }

const initFormData = {
	productList: [],
	orderInfo: {
		name: '',
		contact: '',
		email: ''
	},
	shipping: {
		name: '',
		contact: '',
		postalCode: '',
		roadAddress: '',
		detailAddress: '',
		memo: ''
	},
	payment: {
		totalPrice: '',
		productsPrice: '',
		shippingFee: '',
		depositor: ''
	},
	receipt: {
		type: Object.keys(RECEIPT_TYPE.forPersonal)[0],
		info: ''
	}
};

// TODO: 입력필드 유효성 검사
export default function Order(props) {
	const location = useLocation();
	const navigate = useNavigate();

	const orderItems = location.state.orderItems;
	const [formData, setFormData] = React.useState(initFormData);
	const { data, loading, error } = useFetch(userApi.getUserInfo, []);

	useEffect(() => {
		if (!orderItems || orderItems.length === 0) {
			// 주문할 상품이 없으면 메인으로 이동
			navigate('/');
			return;
		}

		setFormData(prev => ({
			...prev,
			productList: orderItems
		}));
	}, [orderItems, navigate]);

	useEffect(() => {
		if(data) {
			setFormData(prev => ({
				...prev,
				orderInfo: {
					name: data.name,
					contact: data.contact,
					email: data.email
				}
			}));
		}
	}, [data]);
	
	if(loading) {
		return ;
	}

	if(error) {
		alert('사용자 정보 로드에 실패했습니다.');
		navigate('/');
		return ;
	}

	const onHandleSubmit = (productsPrice, shippingFee) => {
		const finalFormData = {
			...formData,
			payment: {
				...formData.payment,
				productsPrice,
				shippingFee,
				totalPrice: productsPrice + shippingFee
			}
		};
		
		console.log(finalFormData);
		orderApi.createOrder(finalFormData)
			.then((response) => {
				navigate('/order/success', { state: { orderNumber: response.orderNumber } });
			})
			.catch((error) => {
				alert('주문에 실패했습니다. 다시 시도해주세요.');
			});
	};

  	return (
		<Section>
			<OrderContainer>
				<LeftSection>
					<PageTitle>주문/결제</PageTitle>
					<CartList
						$useCheckbox={ false }
                        items={ orderItems }
						selectedItems={ new Array(orderItems.length).fill(true) }
						isActive={ false }
					/>
					<OrderForm formData={formData} setFormData={setFormData} />
					<ShippingForm formData={formData} setFormData={setFormData}  ordererInfo= { data }/>
					<PaymentForm formData={formData} setFormData={setFormData} />
				</LeftSection>
				<RightSection>
					<Calculator
						title="결제금액"
						items={ orderItems }
						selectedItems={ new Array(orderItems.length).fill(true) }
						onClickOrder= { onHandleSubmit }
					/>
				</RightSection>
			</OrderContainer>
		</Section>
  	)
}

function OrderForm(props) {
	const handleOrderChange = (e) => {
		props.setFormData({
			...props.formData,
			orderInfo: {
				...props.formData.orderInfo,
				[e.target.name]: e.target.value
			}
		});
	}

	return (
		<FormContainer>
			<FormTitle>주문 정보</FormTitle>
			<Field title="주문자" type="text"
				name="name" value={ props.formData.orderInfo.name }
				onHandleChange={ handleOrderChange }
			/>
			<Field title="연락처" type="tel" $placeholder="'-' 없이 입력하세요."
				name="contact" value={ props.formData.orderInfo.contact }
				onHandleChange={ handleOrderChange }
			/>
			<Field title="이메일 주소" type="text"
				name="email" value={ props.formData.orderInfo.email }
				onHandleChange={ handleOrderChange }
			/>
		</FormContainer>
	);
}

function ShippingForm(props) {
	const [isSame, setIsSame] = React.useState(false);
	
	const handleCheckbox = () => {
		const newIsSame = !isSame;
		setIsSame(newIsSame);

		if(newIsSame) {
			props.setFormData({
				...props.formData,
				shipping: {
					...props.formData.shipping,
					name: props.formData.orderInfo.name,
					contact: props.formData.orderInfo.contact,
					postalCode: props.ordererInfo.postalCode,
					roadAddress: props.ordererInfo.roadAddress,
					detailAddress: props.ordererInfo.detailAddress,
				}
			});
		}
	}

	const handleShippingChange = (e) => {
		props.setFormData({
			...props.formData,
			shipping: {
				...props.formData.shipping,
				[e.target.name]: e.target.value
			}
		});
		setIsSame(false);
	};

	const handleChangeAddress = ({postalCode, roadAddress}) => {
		props.setFormData({
			...props.formData,
			shipping: {
				...props.formData.shipping,
				postalCode,
				roadAddress
			}
		});
		setIsSame(false);
	};

	return (
		<FormContainer>
			<FormTitleWrapper>
				<FormTitle>배송 정보</FormTitle>
				<Medium size="16px" color={ FONT.deep01 }>
					<input type="checkbox" checked={isSame} onClick={handleCheckbox} />&nbsp;주문자 정보와 동일
				</Medium>
			</FormTitleWrapper>
			<Field title="이름" type="text"
				name="name" value={ props.formData.shipping.name }
				onHandleChange={ handleShippingChange }
			/>
			<Field title="연락처" type="tel" $placeholder="'-' 없이 입력하세요."
				name="contact" value={ props.formData.shipping.contact }
				onHandleChange={ handleShippingChange }
			/>
			<AddressField
				value={{ postalCode: props.formData.shipping.postalCode, roadAddress: props.formData.shipping.roadAddress, detailAddress: props.formData.shipping.detailAddress }}
				onHandleChange={ handleShippingChange } onAddressComplete={ handleChangeAddress }
			/>
			<Field title="배송요청사항" type="text"
				name="memo" value={ props.formData.shipping.memo }
				onHandleChange={ handleShippingChange }
			/>
		</FormContainer>
	);
}

function PaymentForm(props) {
	const [isRequested, setIsRequested] = React.useState(true);
	const [isForPersonal, setIsForPersonal] = React.useState(true);

	const { setFormData, formData } = props;

	const handleReceiptChange = (e) => {
		const newIsRequested = e.target.value === 'request';
		setIsRequested(newIsRequested);

		if(!newIsRequested) {
			setFormData({
				...formData,
				receipt: {
					...formData.receipt,
					type: 'NONE',
					info: null
				}
			});
		}
	};

	const handleTypeChange = () => {
		const newIsForPersonal = !isForPersonal;
		setIsForPersonal(newIsForPersonal);

		if(!newIsForPersonal) {
			setFormData({
				...formData,
				receipt: {
					...formData.receipt,
					type: Object.keys(RECEIPT_TYPE.forBusiness)[0],
					info: ''
				}
			});
		}
	}

	const handlePaymentChange = (e) => {
		props.setFormData({
			...props.formData,
			payment: {
				...props.formData.payment,
				[e.target.name]: e.target.value
			}
		});
	};

	const handleReceiptInfoChange = (e) => {
		props.setFormData({
			...props.formData,
			receipt: {
				...props.formData.receipt,
				[e.target.name]: e.target.value
			}
		});
	};

	return (
		<FormContainer>
			<FormTitle>결제정보</FormTitle>
			<Medium size="16px" color={ FONT.default }><input type="radio" name="payment" defaultChecked />&nbsp;무통장 입금</Medium>
			<Field title="계좌" value="은행 123-456-123456 예금주: 영원씨앤피(주)"/>
			<Field title="입금자명" type="text" $placeholder="주문자명과 입금자명이 다른 경우에만 입력해주세요."
				name="depositor" value={ props.formData.payment.depositor }
				onHandleChange={ handlePaymentChange }
			/>
			<FormTitleWrapper style ={{ marginTop: '20px' }}>
				<FormTitle>현금영수증</FormTitle>
				<Medium size="16px" color={ FONT.default }>
					<input type="radio" name="receipt" value="request"
						checked={ isRequested }
						onClick={ handleReceiptChange }
					/>
					&nbsp;신청
				</Medium>
				<Medium size="16px" color={ FONT.default }>
					<input type="radio" name="receipt" value="noRequest"
						checked={ !isRequested }
						onClick={ handleReceiptChange }
					/>
					&nbsp;미신청
				</Medium>
			</FormTitleWrapper>
			{ 
				isRequested &&
				<FieldContainer style={{ width: '100%' }}>
					<FormButton width="50%"
						color={ isForPersonal ? 'dark' : 'light' }
						onClick={ handleTypeChange }
						value="개인소득공제용"
					/>
					<FormButton width="50%" 
						color={ !isForPersonal ? 'dark' : 'light' }
						onClick={ handleTypeChange }
						value="사업자지출증빙용"
					/>
				</FieldContainer>
			}
			{
				 isRequested &&
				 <FieldContainer style={{ width: '100%' }}>
					<DropDownField list={ isForPersonal ? RECEIPT_TYPE.forPersonal : RECEIPT_TYPE.forBusiness }
						name="type" value={ props.formData.receipt.type }
						onChange={ handleReceiptInfoChange }
					/>
					<TextField $placeholder={"\"-\" 없이 입력해주세요."}
						name="info" value={ props.formData.receipt.info }
						onHandleChange={ handleReceiptInfoChange }
					/>
				</FieldContainer>
			}
		</FormContainer>
	);
}